<template>
<div>
    <table-page
        :client="client"
        @updated="getTaxes"
        :warnings="warnings"
        :changelogs="changelogs"
        :hideTabs="true"
    >
        <fast-table
            :recordLabel="recordLabel"
            :sections="sections"
            :headers="headers"
            :data="tableData"
            :activeRowId="currentRowId"
            :hasFixedSortOrder="true"
            @click="currentRowId = $event.locationKey"
        >
            <template v-slot:table-actions>
                <button class="btn btn-primary btn-collapse" id="addButton" @click="addModalOpen = true">
                    <i class="far fa-fw fa-plus"></i> <span class="btn-text">Add Tax Registration</span>
                </button>
            </template>
        </fast-table>

        <add-form-modal
            v-if="addModalOpen"
            :client="client"
            :tax-registrations="taxRegistrations"
            ref="addModal"
            @updated="getTaxes"
            @close="addModalOpen = false"
        />
    </table-page>

    <sidebar
        v-if="currentRowId"
        :client="client"
        :companyId="currentRow.object.companyId"
        :locationName="currentRow.object.name"
        :companyTaxRegistrations="currentRow.object.taxRegistrations"
        :stateCode="currentRow.object.stateAbbreviation"
        @updated="getTaxes"
        @close="currentRowId = null"
    >
    </sidebar>
</div>
</template>

<script>
import TablePageMixin from '@/mixins/TablePageMixin'
import FastTable from '@/components/FastTable'
import Sidebar from './components/CompanyTaxes/Sidebar'
import AddFormModal from './components/CompanyTaxes/AddFormModal'

export default {
    components: {FastTable, Sidebar, AddFormModal},
    mixins: [TablePageMixin],
    props: ['client'],
    computed: {
        currentRow() {
            for (const section of this.tableData) {
                for (const row of section) {
                    if (row.id == this.currentRowId) {
                        return row
                    }
                }
            }

            return null
        },
        sections() {
            const result = []
            this.client.companies.forEach(company => {
                result.push({title: company.name, defaultIsClosed: false, id: company.id, hasDateFilter: false})
            })

            result.sort((a, b) => {
                return a.title > b.title ? 1 : -1
            })

            return result
        },
        taxRegistrationsByLocation() {
            const result = {}

            for (const tr of this.taxRegistrations) {
                const locationKey = [
                    tr.company_id,  // NB: we include this to help separate by sections
                    tr.sym_tax.state_code,
                    tr.sym_tax.county_code,
                    tr.sym_tax.city_code,
                    tr.sym_tax.municipality_code,
                    tr.sym_tax.school_code,
                    tr.sym_tax.transit_district_code,
                    tr.sym_tax.psd_code,
                ].join('-')

                if (!result.hasOwnProperty(locationKey)) {
                    let name;

                    if (tr.sym_tax.state_code == '00') {
                        name = 'Federal'
                    }
                    else if (
                        !tr.sym_tax.county_code &&
                        !tr.sym_tax.city_code &&
                        !tr.sym_tax.municipality_code &&
                        !tr.sym_tax.school_code &&
                        !tr.sym_tax.transit_district_code &&
                        !tr.sym_tax.psd_code
                    ) {
                        name = tr.sym_tax.state_name
                    }
                    else {
                        name = [
                            tr.sym_tax.school_code ? tr.sym_tax.school_name : '',
                            tr.sym_tax.municipality_code ? tr.sym_tax.municipality_name : '',
                            tr.sym_tax.city_code ? tr.sym_tax.city_name : '',
                            tr.sym_tax.county_name ? `${tr.sym_tax.county_name} County` : '',
                            tr.sym_tax.state_abbreviation,
                        ].filter(Boolean).join(', ')
                    }

                    result[locationKey] = {
                        taxRegistrations: [],
                        name: name,
                        locationKey: locationKey,
                        companyId: tr.company_id,

                        stateName: tr.sym_tax.state_name,
                        stateAbbreviation: tr.sym_tax.state_abbreviation,
                        cityName: tr.sym_tax.city_name,
                        countyName: tr.sym_tax.county_name,
                        municipalityName: tr.sym_tax.municipality_name,
                        schoolDistrictNname: tr.sym_tax.school_name,

                    }
                }

                result[locationKey].taxRegistrations.push(tr)
            }

            Object.keys(result).forEach(locationKey => {
                const loc = result[locationKey]
                loc.taxRegistrations.sort((a, b) => {
                    return a.sym_tax.sort_priority > b.sym_tax.sort_priority ? 1 : -1
                })
            })

            return Array.from(Object.values(result))
        },
        tableData() {
            const tableData = []
            this.sections.forEach(_ => {
                tableData.push([])
            })

            if (!this.taxRegistrations.length) {
                return tableData
            }

            this.sections.forEach((section, sectionIdx) => {
                const companyId = section.id
                const locationGroups = this.taxRegistrationsByLocation.filter(x => x.companyId == companyId)

                locationGroups.forEach(lg => {
                    if (!lg.taxRegistrations.length) {return}

                    const row = {
                        id: lg.locationKey,
                        object: lg,
                        cells: [],
                        subrows: [],
                    }

                    lg.taxRegistrations.forEach((tr, idx) => {
                        if (idx == 0) {
                            row.cells = [
                                lg.name,
                                tr.status_pretty,
                                tr.sym_tax.description,
                                tr.sym_tax.tax_type_pretty,
                                tr.is_voluntary ? 'Yes' : ' ',
                                tr.is_exempt ? 'Yes' : ' ',
                                tr.rate ? tr.rate : ' ',
                                tr.filing_id || ' ',
                            ]
                        }
                        else {
                            row.subrows.push([
                                '',
                                tr.status_pretty,
                                tr.sym_tax.description,
                                tr.sym_tax.tax_type_pretty,
                                tr.is_voluntary ? 'Yes' : ' ',
                                tr.is_exempt ? 'Yes' : ' ',
                                tr.rate ? `${tr.rate}%` : ' ',
                                tr.filing_id || ' ',
                            ])
                        }

                    })

                    tableData[sectionIdx].push(row)
                })

                // Sort by priority, then alphabetically by location name
                tableData[sectionIdx].sort((a, b) => {
                    const priorityA = a.object.taxRegistrations[0].sym_tax.sort_priority
                    const priorityB = b.object.taxRegistrations[0].sym_tax.sort_priority

                    if (priorityA == priorityB) {
                        return a.object.name > b.object.name ? 1 : -1
                    }
                    return priorityA > priorityB ? 1 : -1
                })
            })

            return tableData
        },
    },
    data() {
        return {
            taxRegistrations: [],
            dataLoaded: false,
            currentRowId: null,
            recordLabel: "Tax Registrations",
            headers: [
                {label: 'Jurisdiction', classes: '', isSearchable: false, isFilterable: true},
                {label: 'Status', classes: 'cw-4', isSearchable: false, isFilterable: true},
                {label: 'Tax Name', classes: '', isSearchable: true, },
                {label: 'Type', classes: 'cw-12', isSearchable: false, isFilterable: true},
                {label: 'Voluntary?', classes: '', isSearchable: false, isFilterable: true},
                {label: 'Exempt?', classes: '', isSearchable: false, isFilterable: true},
                {label: 'Rate', classes: '', isSearchable: false, isFilterable: false},
                {label: 'Filing ID', classes: '', isSearchable: true},
            ],
            addModalOpen: false
        }
    },
    mounted() {
        this.$store.dispatch('SET_PAGE_TITLE', 'Settings / Taxes')
        this.getTaxes()
    },
    methods: {
        getTaxes() {
            this.$store.dispatch('START_LOADING')
            this.$api.get(`/clients/${this.client.id}/settings/tax-registrations`).then(resp => {
                this.taxRegistrations = resp
                this.$store.dispatch('STOP_LOADING')
            }).catch((errors) => {
                this.$store.dispatch('STOP_LOADING')
                if (errors.__status__ == 403) {
                    this.$bus.$emit('no-access')
                }
                else {
                    this.$bus.showError(errors.__all__)
                }
            })
        },
    },
}
</script>
